"use client";

import { useEffect, useRef, useState } from "react";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { decodeEmail, cloakMailtoLinks } from "@/lib/obfuscateEmail";

/**
 * The one place admin-authored rich text (blog posts, content blocks, standalone pages) turns
 * into real DOM -- sanitizes it (as every dangerouslySetInnerHTML site in this app already did),
 * then de-fangs any mailto: link it contains before that HTML ever reaches the page.
 *
 * This has to work on the *rendered HTML string* itself, not a fixed React component, because
 * the email address isn't always a hardcoded JSX literal -- it can come from Devina's own
 * WYSIWYG-edited content (e.g. the Refunds page's "or email hello@hoopyogi.com" line), so the
 * transform has to run generically on whatever HTML lands here rather than only covering one
 * known address. It runs before sanitizeHtml touches the markup, walking the raw string for any
 * `mailto:` href and swapping both the href and its visible text for a shifted, non-email-shaped
 * placeholder ("hello (at) hoopyogi (dot) com") -- what a scraper that only reads the static
 * page source sees. A layout effect right after mount then finds those placeholders and restores
 * the real, clickable mailto link, which happens fast enough that a real visitor (anyone with
 * JavaScript on, i.e. effectively everyone) never perceives a delay.
 */
export default function SanitizedHtml({ html, className }: { html: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  // Identical on the server and on the client's first (pre-hydration-effect) render -- no
  // window-based branching at this stage, which would render something different from what the
  // server actually sent and trip a hydration mismatch. Mailto-cloaking is pure string
  // replacement (safe anywhere); DOMPurify itself needs a real DOM, so sanitizeHtml only ever
  // runs in the effect below, once this has actually mounted in a browser -- same reasoning as
  // CmsPageView's identical fallback-then-effect split.
  const [rendered, setRendered] = useState(() => cloakMailtoLinks(html));

  useEffect(() => {
    setRendered(sanitizeHtml(cloakMailtoLinks(html)));
  }, [html]);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    container.querySelectorAll<HTMLElement>("[data-cloaked-mailto]").forEach((el) => {
      const encoded = el.dataset.cloakedMailto;
      if (!encoded) return;
      const email = decodeEmail(encoded);
      const link = document.createElement("a");
      link.href = `mailto:${email}`;
      link.textContent = email;
      el.replaceWith(link);
    });
  }, [rendered]);

  return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: rendered }} />;
}
